/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useState } from 'react';

const API_BASE = '/api';
const POLL_INTERVAL_MS = 60_000;

/**
 * Sticky banner that detects when a new backend revision has been deployed
 * while users are in the app, and prompts them to refresh.
 *
 * Mechanism: poll `/api/health` every 60s. The first response establishes
 * the baseline version for this tab. If a later response reports a different
 * version, a banner appears with a "Refresh now" action.
 *
 * The version comes from the APP_VERSION env var which `deploy-clean.ps1`
 * sets on every Container App update.
 */
export function VersionWatcher() {
  const [baselineVersion, setBaselineVersion] = useState<string | null>(null);
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(`${API_BASE}/health`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json() as { version?: string };
        const v = data.version;
        if (!v || cancelled) return;
        setServerVersion(v);
        setBaselineVersion((prev) => prev ?? v);
      } catch {
        // Network hiccup — ignore; will retry next tick.
      }
    }

    poll();
    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  const newVersionAvailable =
    baselineVersion !== null &&
    serverVersion !== null &&
    baselineVersion !== serverVersion;

  if (!newVersionAvailable || dismissed) return null;

  return (
    <div className="bg-amber-500 text-black py-2 px-4 shrink-0 flex items-center justify-center gap-3 text-sm font-medium">
      <span>
        A new version ({serverVersion}) has been deployed. Please refresh to continue — any unsaved work in progress may be lost.
      </span>
      <button
        onClick={() => window.location.reload()}
        className="px-3 py-0.5 rounded bg-black text-amber-200 hover:bg-gray-800 text-xs font-semibold"
      >
        Refresh now
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-black/60 hover:text-black text-base leading-none"
        aria-label="Dismiss"
        title="Dismiss (you will still need to refresh eventually)"
      >
        ✕
      </button>
    </div>
  );
}
